import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TrendingUp, TrendingDown, AlertTriangle, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * FinanceDashboard — Компактный финансовый блок для BI Report Builder
 * Per ТЗ Finance Control (P0 BI-ADVANCED)
 *
 * Compact inline mode: показывает ключевую метрику с popover для деталей
 */

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'decimal',
    maximumFractionDigits: 0,
  }).format(value) + ' ₸';
};

const formatPercent = (value) => {
  if (value == null) return '—';
  return `${value.toFixed(1)}%`;
};

const getMarginColorClasses = (color) => {
  switch (color) {
    case 'green':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'yellow':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'red':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-50 text-gray-500 border-gray-200';
  }
};

const getMarginTextColor = (color) => {
  switch (color) {
    case 'green': return 'text-green-600';
    case 'yellow': return 'text-yellow-600';
    case 'red': return 'text-red-600';
    default: return 'text-gray-600';
  }
};

const FinanceDashboard = ({ summary, onDrillDown }) => {
  if (!summary) {
    return (
      <div className="flex items-center gap-1.5 text-gray-400">
        <span className="w-3.5 h-3.5 flex items-center justify-center font-bold text-[10px]">₸</span>
        <span className="text-xs">Нет данных</span>
      </div>
    );
  }

  const {
    hardware_margin,
    hardware_margin_percent,
    hardware_margin_color,
    service_margin,
    service_margin_percent,
    service_margin_color,
    total_margin,
    total_margin_percent,
    total_margin_color,
    total_revenue,
    budget_plan,
    budget_fact,
    costs_fact,
    remaining,
    projects_at_risk,
    projects_negative_margin,
  } = summary;

  const hasPlanFactData = budget_plan > 0 || costs_fact > 0 || budget_fact > 0;
  const hasWarnings = projects_at_risk > 0 || projects_negative_margin > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-1.5 px-2 py-0.5 rounded transition-all hover:bg-gray-100",
            hasWarnings && "hover:bg-red-50"
          )}
          data-testid="finance-dashboard"
        >
          <span className="w-3.5 h-3.5 flex items-center justify-center font-bold text-[10px] text-gray-400">₸</span>
          <span className="text-xs text-gray-500">Маржа:</span>
          <span className={cn("text-sm font-semibold", getMarginTextColor(total_margin_color))}>
            {formatPercent(total_margin_percent)}
          </span>
          {total_margin != null && (
            total_margin >= 0
              ? <TrendingUp className="w-3 h-3 text-green-500" />
              : <TrendingDown className="w-3 h-3 text-red-500" />
          )}
          {hasWarnings && <AlertTriangle className="w-3 h-3 text-red-500" />}
          <ChevronDown className="w-3 h-3 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        {/* Header */}
        <div className="flex items-center gap-2 text-gray-600 text-xs mb-2 pb-2 border-b">
          <span className="w-3 h-3 flex items-center justify-center font-bold text-[10px]">₸</span>
          <span className="font-medium">Финансовый контроль</span>
          {hasWarnings && (
            <span className="ml-auto text-red-500 text-[10px]">
              {projects_at_risk > 0 && `${projects_at_risk} в риске`}
              {projects_at_risk > 0 && projects_negative_margin > 0 && ', '}
              {projects_negative_margin > 0 && `${projects_negative_margin} убыточных`}
            </span>
          )}
        </div>

        {/* Маржинальность - компактный вид */}
        <div className="text-[10px] uppercase text-gray-400 tracking-wide mb-1">Маржинальность</div>

        <div className="grid grid-cols-2 gap-1 mb-2">
          {/* Железо */}
          <div
            className={cn(
              "px-2 py-1 rounded border text-center cursor-pointer hover:shadow-sm",
              getMarginColorClasses(hardware_margin_color)
            )}
            onClick={() => onDrillDown?.('hardware_margin')}
          >
            <div className="text-[10px] opacity-70">Железо</div>
            <div className="text-sm font-bold">{formatPercent(hardware_margin_percent)}</div>
            <div className="text-[10px] opacity-60">{formatCurrency(hardware_margin)}</div>
          </div>

          {/* Услуги */}
          <div
            className={cn(
              "px-2 py-1 rounded border text-center cursor-pointer hover:shadow-sm",
              getMarginColorClasses(service_margin_color)
            )}
            onClick={() => onDrillDown?.('service_margin')}
          >
            <div className="text-[10px] opacity-70">Услуги</div>
            <div className="text-sm font-bold">{formatPercent(service_margin_percent)}</div>
            <div className="text-[10px] opacity-60">{formatCurrency(service_margin)}</div>
          </div>
        </div>

        {/* Итоговая маржа */}
        <div
          className={cn(
            "px-2 py-1.5 rounded border cursor-pointer hover:shadow-sm mb-2",
            getMarginColorClasses(total_margin_color)
          )}
          onClick={() => onDrillDown?.('total_margin')}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] opacity-70">Итоговая маржа</div>
              <div className="text-base font-bold flex items-center gap-1">
                {formatPercent(total_margin_percent)}
                {total_margin != null && (
                  total_margin >= 0
                    ? <TrendingUp className="w-3 h-3" />
                    : <TrendingDown className="w-3 h-3" />
                )}
              </div>
            </div>
            <div className="text-right text-[10px] opacity-70">
              <div>{formatCurrency(total_margin)}</div>
              <div>из {formatCurrency(total_revenue)}</div>
            </div>
          </div>
        </div>

        {/* План/Факт */}
        {hasPlanFactData && (
          <>
            <div className="text-[10px] uppercase text-gray-400 tracking-wide mb-1">План / Факт</div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500">Бюджет:</span>
                <span className="font-medium truncate ml-1">{formatCurrency(budget_plan)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Затрачено:</span>
                <span className="font-medium text-red-600 truncate ml-1">{formatCurrency(costs_fact)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Получено:</span>
                <span className="font-medium text-green-600 truncate ml-1">{formatCurrency(budget_fact)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Остаток:</span>
                <span className={cn(
                  "font-medium truncate ml-1",
                  remaining > 0 ? "text-green-600" : remaining < 0 ? "text-red-600" : "text-gray-500"
                )}>
                  {formatCurrency(remaining)}
                </span>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default FinanceDashboard;
